import { fbApp, getAnalyticsConsent } from "../firebase";

export type AnalyticsSource =
  | "header"
  | "cta"
  | "profile"
  | "avatar_gear"
  | "features_modal"
  | "item_modal"
  | "config_modal"
  | "board_badge"
  | "post_signin";

type CheckoutTier = "supporter" | "pro";
type SignUpMethod = "google" | "password" | "anonymous";

export type GatedFeature = "pinned_card";

async function tryLogEvent(eventName: string, params: Record<string, unknown>): Promise<void> {
  if (getAnalyticsConsent() !== "accepted") return;
  const { getAnalytics, logEvent } = await import("firebase/analytics");
  const analytics = getAnalytics(fbApp);
  logEvent(analytics, eventName, params);
}

// SPA route-change page_view. Firebase Analytics auto-fires ONE page_view on
// SDK init (i.e. whichever URL was first landed on); everything after that has
// to be fired manually or GA reports 0 views for internal navigation.
export function logPageView(pageLocation: string, pageTitle: string): Promise<void> {
  return tryLogEvent("page_view", { page_location: pageLocation, page_title: pageTitle });
}

export function logPricingPageViewed(source: AnalyticsSource): Promise<void> {
  return tryLogEvent("pricing_page_viewed", { source });
}

export function logUpgradeCtaClicked(tier: CheckoutTier, surface: AnalyticsSource): Promise<void> {
  return tryLogEvent("upgrade_cta_clicked", { tier, surface });
}

export function logCheckoutSessionCreated(tier: CheckoutTier, amount?: number): Promise<void> {
  const params: Record<string, unknown> = { tier };
  if (typeof amount === "number") params.amount = amount;
  return tryLogEvent("checkout_session_created", params);
}

export function logSignUp(method: SignUpMethod): Promise<void> {
  return tryLogEvent("sign_up", { method });
}

export function logUpsellSurfaceShown(surface: AnalyticsSource, tierGated: CheckoutTier): Promise<void> {
  return tryLogEvent("upsell_surface_shown", { surface, tier_gated: tierGated });
}

export function logUpsellSurfaceClicked(surface: AnalyticsSource, tierGated: CheckoutTier): Promise<void> {
  return tryLogEvent("upsell_surface_clicked", { surface, tier_gated: tierGated });
}

export function logPurchaseCompleted(tier: CheckoutTier): Promise<void> {
  return tryLogEvent("purchase_completed", { tier });
}

export function logBoardCreated(): Promise<void> {
  return tryLogEvent("board_created", {});
}

export function logFirstItemAdded(): Promise<void> {
  return tryLogEvent("first_item_added", {});
}

// Fires `first_item_added` at most once per browser. localStorage-gated so it
// survives reload but resets if the user clears site data. Acceptable lossy
// dedup — overcounting on cleared storage is preferable to a per-write RTDB
// check on every item add.
const FIRST_ITEM_KEY = "retroboard-first-item-added";
export function logFirstItemAddedIfFirstTime(): Promise<void> {
  try {
    if (localStorage.getItem(FIRST_ITEM_KEY) === "1") return Promise.resolve();
    localStorage.setItem(FIRST_ITEM_KEY, "1");
  } catch {
    // localStorage unavailable — fall through and fire anyway
  }
  return logFirstItemAdded();
}

export function logGatedFeatureAttempted(feature: GatedFeature, tierRequired: CheckoutTier): Promise<void> {
  return tryLogEvent("gated_feature_attempted", { feature, tier_required: tierRequired });
}
